import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AdminUser,
  AdminZone,
  AdminPoint,
  DayStat,
  RecentEvent,
  ImportResult,
  MazeResult,
  ConnectionStats,
  ConnectionInfo,
  getAdminUsers,
  updateAdminUser,
  deleteAdminUser,
  getAdminStats,
  getAdminEvents,
  importImage,
  getAdminZones,
  adminDeleteZone,
  adminCreateZone,
  adminUpdateZone,
  generateMaze,
  getAdminPoints,
  adminCreatePoint,
  adminUpdatePoint,
  adminEmitDialog,
  getConnectionStats,
  getConnections,
} from '../api/admin';
import { WorldCanvas, Viewport } from '../components/WorldCanvas/WorldCanvas';
import { ZoneOverlay } from '../components/ZoneOverlay/ZoneOverlay';
import { ZoneDrawOverlay } from '../components/ZoneOverlay/ZoneDrawOverlay';
import { Zone } from '../types/zone';
import { getZonesInArea } from '../api/zones';
import { WebSocketClient, WsEvent, WsStatusInfo, getWsBase } from '../api/websocket';
import { ConnectionDot } from '../components/ConnectionDot/ConnectionDot';
import { Point } from '../types/point';
import { FreeBoundaryOverlay } from '../components/ZoneOverlay/FreeBoundaryOverlay';
import {
  DEFAULT_SPEECH_STYLE,
  SPEECH_STYLE_OPTIONS,
  SpeechBubbleStyle,
} from '../utils/speechStyles';
import '../styles/admin.scss';

type Tab = 'users' | 'stats' | 'events' | 'import' | 'zones' | 'maze' | 'dialogue' | 'connections';

// ─── UserSearchInput ──────────────────────────────────────────────────────────

interface UserSearchInputProps {
  userId  : number | null;
  onChange: (userId: number, email: string) => void;
}

const UserSearchInput: React.FC<UserSearchInputProps> = ({ userId, onChange }) => {
  const { t } = useTranslation();
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState<AdminUser[]>([]);
  const [open,    setOpen]    = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef  = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const search = (q: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    timerRef.current = setTimeout(() => {
      getAdminUsers(q, 10, 0)
        .then(res => { setResults(res.data.users ?? []); setOpen(true); })
        .catch(() => {});
    }, 300);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className="auth-form__input"
        placeholder={t('admin.zones.userSearchPlaceholder')}
        value={query}
        onChange={e => { setQuery(e.target.value); search(e.target.value); }}
        onFocus={() => results.length > 0 && setOpen(true)}
      />
      {userId !== null && !open && (
        <span style={{ fontSize: '11px', opacity: 0.6, marginLeft: '4px' }}>id: {userId}</span>
      )}
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: '#1c1c1c', border: '1px solid #333', maxHeight: '180px', overflowY: 'auto',
        }}>
          {results.map(u => (
            <div
              key={u.id}
              style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #2a2a2a' }}
              onMouseDown={() => {
                setQuery(u.email);
                onChange(u.id, u.email);
                setOpen(false);
              }}
            >
              <span style={{ opacity: 0.45, marginRight: '6px' }}>#{u.id}</span>{u.email}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface PointSearchInputProps {
  pointId  : number | null;
  onChange : (point: AdminPoint) => void;
}

const PointSearchInput: React.FC<PointSearchInputProps> = ({ pointId, onChange }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AdminPoint[]>([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const search = (q: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    timerRef.current = setTimeout(() => {
      getAdminPoints(q, 10, 0)
        .then(res => { setResults(res.data.points ?? []); setOpen(true); })
        .catch(() => {});
    }, 300);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className="auth-form__input"
        placeholder={t('admin.dialogue.pointSearchPlaceholder')}
        value={query}
        onChange={e => { setQuery(e.target.value); search(e.target.value); }}
        onFocus={() => results.length > 0 && setOpen(true)}
      />
      {pointId !== null && !open && (
        <span style={{ fontSize: '11px', opacity: 0.6, marginLeft: '4px' }}>id: {pointId}</span>
      )}
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: '#1c1c1c', border: '1px solid #333', maxHeight: '220px', overflowY: 'auto',
        }}>
          {results.map(p => (
            <div
              key={p.id}
              style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #2a2a2a' }}
              onMouseDown={() => {
                setQuery(`#${p.id} ${p.name}`);
                onChange(p);
                setOpen(false);
              }}
            >
              <span style={{ opacity: 0.45, marginRight: '6px' }}>#{p.id}</span>
              {p.name || '—'} · {p.user_email} · ({p.x}, {p.y})
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── AdminMapSelectOverlay ────────────────────────────────────────────────────

interface AdminMapSelectOverlayProps {
  initialX1?: number; initialY1?: number;
  initialX2?: number; initialY2?: number;
  onSelect : (x1: number, y1: number, x2: number, y2: number) => void;
  onCancel : () => void;
}

const AdminMapSelectOverlay: React.FC<AdminMapSelectOverlayProps> = ({
  initialX1, initialY1, initialX2, initialY2, onSelect, onCancel,
}) => {
  const { t } = useTranslation();

  const [viewport,   setViewport]   = useState<Viewport>({ x: 0, y: 0, zoom: 1 });
  const [points,     setPoints]     = useState<Point[]>([]);
  const [zones,      setZones]      = useState<Zone[]>([]);
  const [drawMode,   setDrawMode]   = useState(false);
  const [coordInput, setCoordInput] = useState('');
  const [winSize,    setWinSize]    = useState({ width: window.innerWidth, height: window.innerHeight });
  const vpRef      = useRef(viewport);
  vpRef.current    = viewport;
  const winRef     = useRef(winSize);
  winRef.current   = winSize;
  const [wsStatus, setWsStatus] = useState<WsStatusInfo | null>(null);
  const wsRef      = useRef<WebSocketClient | null>(null);
  const zoneTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resubTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !drawMode) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawMode, onCancel]);

  useEffect(() => {
    const onResize = () => setWinSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Guest WebSocket ────────────────────────────────────────────
  const sendSubscribe = useCallback((vp: Viewport) => {
    const { width: w, height: h } = winRef.current;
    const halfW = Math.ceil(w / 2 / vp.zoom) + 10;
    const halfH = Math.ceil(h / 2 / vp.zoom) + 10;
    wsRef.current?.send({
      type: 'subscribe',
      area: {
        x_min: Math.round(vp.x) - halfW, y_min: Math.round(vp.y) - halfH,
        x_max: Math.round(vp.x) + halfW, y_max: Math.round(vp.y) + halfH,
      },
    });
  }, []);

  useEffect(() => {
    const wsBase = getWsBase();
    const initVp = vpRef.current;
    const { width: w, height: h } = winRef.current;
    const halfW = Math.ceil(w / 2 / initVp.zoom) + 10;
    const halfH = Math.ceil(h / 2 / initVp.zoom) + 10;
    const url = `${wsBase}?token=guest&area=${Math.round(initVp.x)-halfW},${Math.round(initVp.y)-halfH},${Math.round(initVp.x)+halfW},${Math.round(initVp.y)+halfH}`;

    const client = new WebSocketClient(url, (event: WsEvent) => {
      if (event.type === 'init' || event.type === 'points_update') {
        setPoints(Array.isArray(event.points) ? event.points : []);
      } else if (event.type === 'moved') {
        setPoints(prev =>
          prev.map(p => p.id === event.pointId ? { ...p, x: event.newX, y: event.newY } : p),
        );
      }
    }, setWsStatus);
    wsRef.current = client;
    return () => {
      client.close();
      wsRef.current = null;
      if (resubTimer.current) clearTimeout(resubTimer.current);
      if (zoneTimer.current)  clearTimeout(zoneTimer.current);
    };
  }, []);

  const fetchZones = useCallback((vp: Viewport) => {
    const { width: w, height: h } = winRef.current;
    const halfW = Math.ceil(w / 2 / vp.zoom) + 20;
    const halfH = Math.ceil(h / 2 / vp.zoom) + 20;
    getZonesInArea(
      Math.round(vp.x) - halfW, Math.round(vp.y) - halfH,
      Math.round(vp.x) + halfW, Math.round(vp.y) + halfH,
    ).then(res => setZones(res.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchZones(vpRef.current); }, [fetchZones]);

  const handleVpChange = useCallback((vp: Viewport) => {
    setViewport(vp);
    if (resubTimer.current) clearTimeout(resubTimer.current);
    resubTimer.current = setTimeout(() => sendSubscribe(vp), 250);
    if (zoneTimer.current) clearTimeout(zoneTimer.current);
    zoneTimer.current = setTimeout(() => fetchZones(vp), 500);
  }, [sendSubscribe, fetchZones]);

  const handleGoTo = () => {
    const parts = coordInput.split(',').map(s => parseInt(s.trim(), 10));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      handleVpChange({ ...vpRef.current, x: parts[0], y: parts[1] });
      setCoordInput('');
    }
  };

  // Navigate to initial coords on mount
  useEffect(() => {
    if (initialX1 != null && initialX2 != null && initialY1 != null && initialY2 != null) {
      const cx = (initialX1 + initialX2) / 2;
      const cy = (initialY1 + initialY2) / 2;
      setViewport(vp => ({ ...vp, x: cx, y: cy }));
    }
  }, []); // eslint-disable-line

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: '#000' }}>
      {/* Map */}
      <WorldCanvas
        points={points}
        viewport={viewport}
        width={winSize.width}
        height={winSize.height}
        onViewportChange={handleVpChange}
      />
      <FreeBoundaryOverlay viewport={viewport} width={winSize.width} height={winSize.height} />
      <ZoneOverlay
        zones={zones}
        viewport={viewport}
        width={winSize.width}
        height={winSize.height}
      />
      {drawMode && (
        <ZoneDrawOverlay
          viewport={viewport}
          width={winSize.width}
          height={winSize.height}
          onSelect={(x1, y1, x2, y2) => { setDrawMode(false); onSelect(x1, y1, x2, y2); }}
          onCancel={() => setDrawMode(false)}
        />
      )}

      {/* Top bar with coords + navigation */}
      {!drawMode && (
        <div style={{
          position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
          zIndex: 10, display: 'flex', gap: '6px', alignItems: 'center',
          background: 'rgba(0,0,0,0.7)', padding: '6px 12px', borderRadius: '4px',
        }}>
          <span style={{ fontSize: '11px', opacity: 0.6, fontFamily: 'monospace' }}>
            X:{Math.round(viewport.x)} Y:{Math.round(viewport.y)}
          </span>
          <input
            style={{
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
              color: '#fff', fontFamily: 'monospace', fontSize: '11px', padding: '2px 6px', width: '90px',
            }}
            placeholder="x, y"
            value={coordInput}
            onChange={e => setCoordInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleGoTo()}
          />
          <button className="admin-btn admin-btn--sm" onClick={handleGoTo}>GO</button>
          <span style={{ width: '1px', background: 'rgba(255,255,255,0.2)', alignSelf: 'stretch', margin: '0 4px' }} />
          <button className="admin-btn" onClick={() => setDrawMode(true)}>
            {t('zone.drawBtn')}
          </button>
          <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={onCancel}>✕</button>
          <ConnectionDot statusInfo={wsStatus} />
        </div>
      )}
    </div>
  );
};

type DialogueMapMode = 'pick-existing' | 'pick-position';

interface DialogueMapOverlayProps {
  actorLabel: string;
  mode: DialogueMapMode;
  initialX?: number;
  initialY?: number;
  selectedX?: number;
  selectedY?: number;
  onPickExisting: (pointId: number) => void;
  onPickPosition: (x: number, y: number) => void;
  onCancel: () => void;
}

const DialogueMapOverlay: React.FC<DialogueMapOverlayProps> = ({
  actorLabel,
  mode,
  initialX,
  initialY,
  selectedX,
  selectedY,
  onPickExisting,
  onPickPosition,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [viewport, setViewport] = useState<Viewport>({ x: initialX ?? 0, y: initialY ?? 0, zoom: 12 });
  const [points, setPoints] = useState<Point[]>([]);
  const [coordInput, setCoordInput] = useState('');
  const [winSize, setWinSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [wsStatus, setWsStatus] = useState<WsStatusInfo | null>(null);
  const [hint, setHint] = useState('');
  const [hoverPoint, setHoverPoint] = useState<Point | null>(null);
  const vpRef = useRef(viewport);
  vpRef.current = viewport;
  const winRef = useRef(winSize);
  winRef.current = winSize;
  const wsRef = useRef<WebSocketClient | null>(null);
  const resubTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    const onResize = () => setWinSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [onCancel]);

  const sendSubscribe = useCallback((vp: Viewport) => {
    const { width: w, height: h } = winRef.current;
    const halfW = Math.ceil(w / 2 / vp.zoom) + 10;
    const halfH = Math.ceil(h / 2 / vp.zoom) + 10;
    wsRef.current?.send({
      type: 'subscribe',
      area: {
        x_min: Math.round(vp.x) - halfW,
        y_min: Math.round(vp.y) - halfH,
        x_max: Math.round(vp.x) + halfW,
        y_max: Math.round(vp.y) + halfH,
      },
    });
  }, []);

  useEffect(() => {
    const initVp = vpRef.current;
    const { width: w, height: h } = winRef.current;
    const halfW = Math.ceil(w / 2 / initVp.zoom) + 10;
    const halfH = Math.ceil(h / 2 / initVp.zoom) + 10;
    const wsBase = getWsBase();
    const url = `${wsBase}?token=guest&area=${Math.round(initVp.x) - halfW},${Math.round(initVp.y) - halfH},${Math.round(initVp.x) + halfW},${Math.round(initVp.y) + halfH}`;

    const client = new WebSocketClient(url, (event: WsEvent) => {
      if (event.type === 'init' || event.type === 'points_update') {
        setPoints(Array.isArray(event.points) ? event.points : []);
      } else if (event.type === 'moved') {
        setPoints(prev => prev.map(p => p.id === event.pointId ? { ...p, x: event.newX, y: event.newY } : p));
      }
    }, setWsStatus);

    wsRef.current = client;
    return () => {
      client.close();
      wsRef.current = null;
      if (resubTimer.current) clearTimeout(resubTimer.current);
    };
  }, [sendSubscribe]);

  const handleViewportChange = useCallback((vp: Viewport) => {
    setViewport(vp);
    if (resubTimer.current) clearTimeout(resubTimer.current);
    resubTimer.current = setTimeout(() => sendSubscribe(vp), 250);
  }, [sendSubscribe]);

  const handleGoTo = () => {
    const parts = coordInput.split(',').map(s => parseInt(s.trim(), 10));
    if (parts.length === 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
      handleViewportChange({ ...vpRef.current, x: parts[0], y: parts[1] });
      setCoordInput('');
    }
  };

  const handleWorldClick = (worldX: number, worldY: number) => {
    const x = Math.round(worldX);
    const y = Math.round(worldY);
    if (mode === 'pick-existing') {
      const point = points.find(p => p.x === x && p.y === y);
      if (!point) {
        setHint(t('admin.dialogue.mapNoPoint'));
        return;
      }
      onPickExisting(point.id);
      return;
    }
    onPickPosition(x, y);
  };

  const handleWorldHover = useCallback((worldX: number, worldY: number) => {
    if (mode !== 'pick-existing') {
      setHoverPoint(null);
      return;
    }
    const x = Math.round(worldX);
    const y = Math.round(worldY);
    const point = points.find(p => p.x === x && p.y === y) ?? null;
    setHoverPoint(point);
  }, [mode, points]);

  const markerX = hoverPoint?.x ?? selectedX ?? initialX;
  const markerY = hoverPoint?.y ?? selectedY ?? initialY;
  const markerVisible = markerX != null && markerY != null;
  const markerSize = Math.max(viewport.zoom, 16);
  const markerLeft = markerVisible
    ? (markerX - viewport.x) * viewport.zoom + winSize.width / 2 + viewport.zoom / 2
    : 0;
  const markerTop = markerVisible
    ? (viewport.y - markerY) * viewport.zoom + winSize.height / 2 + viewport.zoom / 2
    : 0;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 550, background: '#000' }}>
      <WorldCanvas
        points={points}
        viewport={viewport}
        width={winSize.width}
        height={winSize.height}
        onViewportChange={handleViewportChange}
        onWorldClick={handleWorldClick}
        onWorldHover={handleWorldHover}
      />

      {markerVisible && (
        <div
          className={`admin-dialogue-map-marker admin-dialogue-map-marker--${mode}`}
          style={{
            left: markerLeft,
            top: markerTop,
            width: markerSize,
            height: markerSize,
          }}
        >
          <div className="admin-dialogue-map-marker__label">
            {hoverPoint ? `#${hoverPoint.id} (${markerX}, ${markerY})` : `(${markerX}, ${markerY})`}
          </div>
        </div>
      )}

      <div style={{
        position: 'absolute',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 20,
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
        background: 'rgba(0,0,0,0.8)',
        padding: '8px 12px',
        borderRadius: '4px',
        flexWrap: 'wrap',
        maxWidth: 'min(96vw, 920px)',
      }}>
        <strong style={{ fontSize: '12px', letterSpacing: '1px' }}>{actorLabel}</strong>
        <span className="admin-dim" style={{ fontSize: '12px' }}>
          {mode === 'pick-existing'
            ? t('admin.dialogue.mapPickExisting')
            : t('admin.dialogue.mapPickPosition')}
        </span>
        <span style={{ fontSize: '11px', opacity: 0.65, fontFamily: 'monospace' }}>
          X:{Math.round(viewport.x)} Y:{Math.round(viewport.y)} Z:{viewport.zoom.toFixed(1)}
        </span>
        <input
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff',
            fontFamily: 'monospace',
            fontSize: '11px',
            padding: '2px 6px',
            width: '90px',
          }}
          placeholder="x, y"
          value={coordInput}
          onChange={e => setCoordInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleGoTo()}
        />
        <button className="admin-btn admin-btn--sm" onClick={handleGoTo}>GO</button>
        <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={onCancel}>✕</button>
        <ConnectionDot statusInfo={wsStatus} />
      </div>

      <div style={{
        position: 'absolute',
        left: 12,
        bottom: 12,
        zIndex: 20,
        background: 'rgba(0,0,0,0.8)',
        border: '1px solid rgba(255,255,255,0.12)',
        padding: '10px 12px',
        maxWidth: 'min(92vw, 420px)',
        fontSize: '12px',
        lineHeight: 1.4,
      }}>
        <div>{mode === 'pick-existing' ? t('admin.dialogue.mapPickExistingHint') : t('admin.dialogue.mapPickPositionHint')}</div>
        {hint && <div style={{ marginTop: '6px', color: '#f6c665' }}>{hint}</div>}
      </div>
    </div>
  );
};

// ─── Users tab ───────────────────────────────────────────────────────────────

const UsersTab: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [editMaxPoints, setEditMaxPoints] = useState(30);
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  const limit = 20;

  const load = async (q = search, p = page) => {
    setLoading(true);
    setError('');
    try {
      const res = await getAdminUsers(q, limit, p * limit);
      setUsers(res.data.users ?? []);
      setTotal(res.data.total);
    } catch {
      setError(t('admin.users.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page]); // eslint-disable-line

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    load(search, 0);
  };

  const openEdit = (u: AdminUser) => {
    setEditing(u);
    setEditMaxPoints(u.max_points);
    setEditIsAdmin(u.is_admin);
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await updateAdminUser(editing.id, editMaxPoints, editIsAdmin);
      setEditing(null);
      load();
    } catch (e: any) {
      setError(e.response?.data?.error || t('admin.users.loadError'));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setEditing(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing]);

  const handleDelete = async (u: AdminUser) => {
    if (!window.confirm(t('admin.users.deleteConfirm', { email: u.email }))) return;
    try {
      await deleteAdminUser(u.id);
      load();
    } catch (e: any) {
      setError(e.response?.data?.error || t('admin.users.loadError'));
    }
  };

  const pages = Math.ceil(total / limit);

  return (
    <div className="admin-tab">
      <form className="admin-search" onSubmit={handleSearch}>
        <input
          className="auth-form__input"
          placeholder={t('admin.users.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button className="admin-btn" type="submit">{t('admin.users.searchBtn')}</button>
      </form>

      {error && <div className="admin-error">{error}</div>}
      {loading && <div className="admin-dim">{t('admin.users.loadingText')}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.users.colId')}</th>
              <th>{t('admin.users.colEmail')}</th>
              <th>{t('admin.users.colVerified')}</th>
              <th>{t('admin.users.colAdmin')}</th>
              <th>{t('admin.users.colPixels')}</th>
              <th>{t('admin.users.colMoves')}</th>
              <th>{t('admin.users.colMaxPoints')}</th>
              <th>{t('admin.users.colOAuth')}</th>
              <th>{t('admin.users.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className={u.is_admin ? 'admin-row--admin' : ''}>
                <td>{u.id}</td>
                <td className="admin-cell-email">{u.email}</td>
                <td>{u.is_verified ? '✓' : '—'}</td>
                <td>{u.is_admin ? '★' : '—'}</td>
                <td>{u.point_count}</td>
                <td>{u.move_count}</td>
                <td>{u.max_points}</td>
                <td>{u.oauth_provider || '—'}</td>
                <td>
                  <button className="admin-btn admin-btn--sm" onClick={() => openEdit(u)}>{t('admin.users.editBtn')}</button>
                  {' '}
                  <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => handleDelete(u)}>{t('admin.users.deleteBtn')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="admin-pagination">
          <button className="admin-btn admin-btn--sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>←</button>
          <span className="admin-dim">{page + 1} / {pages}</span>
          <button className="admin-btn admin-btn--sm" disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>→</button>
        </div>
      )}

      {editing && (
        <div className="admin-modal-backdrop" onClick={() => setEditing(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal__title">{t('admin.users.editTitle', { email: editing.email })}</div>
            <label className="auth-form__label">{t('admin.users.maxPointsLabel')}</label>
            <input
              className="auth-form__input"
              type="number"
              min={1}
              value={editMaxPoints}
              onChange={e => setEditMaxPoints(Number(e.target.value))}
            />
            <label className="admin-checkbox">
              <input type="checkbox" checked={editIsAdmin} onChange={e => setEditIsAdmin(e.target.checked)} />
              <span>{t('admin.users.isAdminLabel')}</span>
            </label>
            <div className="admin-modal__actions">
              <button className="admin-btn" onClick={handleSave} disabled={saving}>
                {saving ? t('admin.users.savingBtn') : t('admin.users.saveBtn')}
              </button>
              <button className="admin-btn admin-btn--ghost" onClick={() => setEditing(null)}>{t('admin.users.cancelBtn')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Stats tab ────────────────────────────────────────────────────────────────

const StatsTab: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DayStat[]>([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (d = days) => {
    setLoading(true);
    setError('');
    try {
      const res = await getAdminStats(d);
      setStats(res.data.stats ?? []);
    } catch {
      setError(t('admin.stats.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleDaysChange = (d: number) => {
    setDays(d);
    load(d);
  };

  const maxMoves = Math.max(...stats.map(s => s.moves), 1);
  const totalMoves = stats.reduce((sum, s) => sum + s.moves, 0);
  const totalCreates = stats.reduce((sum, s) => sum + s.creates, 0);
  const totalDeletes = stats.reduce((sum, s) => sum + s.deletes, 0);

  return (
    <div className="admin-tab">
      <div className="admin-stats-controls">
        {[7, 14, 30, 90].map(d => (
          <button
            key={d}
            className={`admin-btn admin-btn--sm${days === d ? ' admin-btn--active' : ''}`}
            onClick={() => handleDaysChange(d)}
          >{t('admin.stats.daysBtn', { days: d })}</button>
        ))}
      </div>

      {error && <div className="admin-error">{error}</div>}
      {loading && <div className="admin-dim">{t('admin.stats.loadingText')}</div>}

      <div className="admin-totals">
        <div className="admin-total-card"><span className="admin-total-val">{totalMoves}</span><span className="admin-dim">{t('admin.stats.moves')}</span></div>
        <div className="admin-total-card"><span className="admin-total-val">{totalCreates}</span><span className="admin-dim">{t('admin.stats.created')}</span></div>
        <div className="admin-total-card"><span className="admin-total-val">{totalDeletes}</span><span className="admin-dim">{t('admin.stats.deleted')}</span></div>
      </div>

      <div className="admin-chart">
        {stats.map(s => (
          <div key={s.day} className="admin-chart__col">
            <div
              className="admin-chart__bar"
              style={{ height: `${Math.round((s.moves / maxMoves) * 100)}%` }}
              title={t('admin.stats.movesTooltip', { day: s.day, count: s.moves })}
            />
            <div className="admin-chart__label">{s.day.slice(5)}</div>
          </div>
        ))}
        {stats.length === 0 && !loading && (
          <div className="admin-dim" style={{ margin: 'auto' }}>{t('admin.stats.noData')}</div>
        )}
      </div>
    </div>
  );
};

// ─── Events tab ───────────────────────────────────────────────────────────────

const EventsTab: React.FC = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<RecentEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    getAdminEvents(50)
      .then(res => setEvents(res.data.events ?? []))
      .catch(() => setError(t('admin.events.loadError')))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const eventLabel = (type: string) => {
    const key = `admin.events.type${type.charAt(0).toUpperCase() + type.slice(1)}` as const;
    return t(key as any, { defaultValue: type });
  };

  return (
    <div className="admin-tab">
      {error && <div className="admin-error">{error}</div>}
      {loading && <div className="admin-dim">{t('admin.events.loadingText')}</div>}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.events.colTime')}</th>
              <th>{t('admin.events.colType')}</th>
              <th>{t('admin.events.colUser')}</th>
              <th>{t('admin.events.colPixel')}</th>
              <th>{t('admin.events.colCoords')}</th>
              <th>{t('admin.events.colColor')}</th>
            </tr>
          </thead>
          <tbody>
            {events.map(e => (
              <tr key={e.id} className={`admin-event--${e.event_type}`}>
                <td className="admin-cell-time">{new Date(e.created_at).toLocaleString()}</td>
                <td><span className={`admin-badge admin-badge--${e.event_type}`}>{eventLabel(e.event_type)}</span></td>
                <td className="admin-cell-email">{e.user_email}</td>
                <td>{e.name}</td>
                <td>
                  {e.event_type === 'moved' && e.old_x != null
                    ? `(${e.old_x},${e.old_y}) → (${e.x},${e.y})`
                    : `(${e.x},${e.y})`}
                </td>
                <td>
                  <span className="admin-color-dot" style={{ background: e.color }} />
                  {e.color}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Import tab ───────────────────────────────────────────────────────────────

const ImportTab: React.FC = () => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [originX, setOriginX] = useState(0);
  const [originY, setOriginY] = useState(0);
  const [colorMode, setColorMode] = useState<'black' | 'white'>('black');
  const [threshold, setThreshold] = useState(128);
  const [color, setColor] = useState('#000000');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setResult(null);
    if (f) {
      const url = URL.createObjectURL(f);
      setPreview(url);
    } else {
      setPreview(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setError(t('admin.import.selectImageError')); return; }
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('origin_x', String(originX));
      fd.append('origin_y', String(originY));
      fd.append('color_mode', colorMode);
      fd.append('threshold', String(threshold));
      fd.append('color', color);
      const res = await importImage(fd);
      setResult(res.data);
    } catch (e: any) {
      setError(e.response?.data?.error || t('admin.import.importError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-tab admin-import">
      <p className="admin-dim" style={{ marginBottom: '1rem', fontSize: '0.72rem' }}>
        {t('admin.import.hint')}
      </p>

      <form className="admin-import-form" onSubmit={handleSubmit}>
        <div className="admin-import-left">
          <div className="auth-form__field">
            <label className="auth-form__label">{t('admin.import.imageLabel')}</label>
            <input ref={fileRef} type="file" accept="image/*" className="admin-file-input" onChange={handleFile} />
          </div>

          {preview && (
            <div className="admin-preview-wrap">
              <img src={preview} alt="preview" className="admin-preview-img" />
            </div>
          )}
        </div>

        <div className="admin-import-right">
          <div className="admin-import-row">
            <div className="auth-form__field" style={{ flex: 1 }}>
              <label className="auth-form__label">{t('admin.import.startXLabel')}</label>
              <input className="auth-form__input" type="number" value={originX} onChange={e => setOriginX(Number(e.target.value))} />
            </div>
            <div className="auth-form__field" style={{ flex: 1 }}>
              <label className="auth-form__label">{t('admin.import.startYLabel')}</label>
              <input className="auth-form__input" type="number" value={originY} onChange={e => setOriginY(Number(e.target.value))} />
            </div>
          </div>

          <div className="auth-form__field">
            <label className="auth-form__label">{t('admin.import.colorModeLabel')}</label>
            <div className="admin-toggle-group">
              <button
                type="button"
                className={`admin-btn admin-btn--sm${colorMode === 'black' ? ' admin-btn--active' : ''}`}
                onClick={() => setColorMode('black')}
              >{t('admin.import.blackBtn')}</button>
              <button
                type="button"
                className={`admin-btn admin-btn--sm${colorMode === 'white' ? ' admin-btn--active' : ''}`}
                onClick={() => setColorMode('white')}
              >{t('admin.import.whiteBtn')}</button>
            </div>
          </div>

          <div className="auth-form__field">
            <label className="auth-form__label">{t('admin.import.thresholdLabel', { value: threshold })}</label>
            <input
              type="range" min={0} max={255}
              value={threshold}
              onChange={e => setThreshold(Number(e.target.value))}
              className="admin-range"
            />
            <span className="admin-dim" style={{ fontSize: '0.65rem' }}>
              {colorMode === 'black'
                ? t('admin.import.thresholdBlack', { value: threshold })
                : t('admin.import.thresholdWhite', { value: threshold })}
            </span>
          </div>

          <div className="auth-form__field">
            <label className="auth-form__label">{t('admin.import.pixelColorLabel')}</label>
            <div className="admin-color-pick">
              <input type="color" value={color} onChange={e => setColor(e.target.value)} className="admin-color-input" />
              <input className="auth-form__input" value={color} onChange={e => setColor(e.target.value)} maxLength={7} style={{ flex: 1 }} />
            </div>
          </div>

          {error && <div className="admin-error">{error}</div>}

          {result && (
            <div className="admin-import-result">
              {t('admin.import.result', { created: result.created, skipped: result.skipped })}
            </div>
          )}

          <button className="auth-form__submit" type="submit" disabled={loading || !file}>
            {loading ? t('admin.import.importingBtn') : t('admin.import.importBtn')}
          </button>
        </div>
      </form>
    </div>
  );
};

// ─── Zones tab ───────────────────────────────────────────────────────────────

const ZonesTab: React.FC = () => {
  const { t } = useTranslation();
  const [zones,   setZones]   = useState<AdminZone[]>([]);
  const [total,   setTotal]   = useState(0);
  const [search,  setSearch]  = useState('');
  const [page,    setPage]    = useState(0);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const limit = 50;

  // Create / Edit modal state
  const [showCreate,   setShowCreate]   = useState(false);
  const [editingZone,  setEditingZone]  = useState<AdminZone | null>(null);
  const [showMap,      setShowMap]      = useState(false);
  const [cUserId,      setCUserId]      = useState<number | null>(null);
  const [cName,        setCName]        = useState('');
  const [cX1,          setCX1]          = useState('');
  const [cY1,          setCY1]          = useState('');
  const [cX2,          setCX2]          = useState('');
  const [cY2,          setCY2]          = useState('');
  const [cTimeout,     setCTimeout]     = useState('30');
  const [cIsFree,      setCIsFree]      = useState(true);
  const [cExpiresAt,   setCExpiresAt]   = useState('');
  const [saving,       setSaving]       = useState(false);
  const [formErr,      setFormErr]      = useState('');

  const load = async (q = search, p = page) => {
    setLoading(true);
    setError('');
    try {
      const res = await getAdminZones(q, limit, p * limit);
      setZones(res.data.zones ?? []);
      setTotal(res.data.total);
    } catch {
      setError(t('admin.zones.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page]); // eslint-disable-line

  useEffect(() => {
    if (!showCreate && !showMap) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setShowCreate(false); setShowMap(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showCreate, showMap]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    load(search, 0);
  };

  const handleDelete = async (z: AdminZone) => {
    if (!window.confirm(t('admin.zones.deleteConfirm', { name: z.name || `#${z.id}` }))) return;
    try {
      await adminDeleteZone(z.id);
      load();
    } catch (e: any) {
      setError(e.response?.data?.error || t('admin.zones.loadError'));
    }
  };

  const resetForm = () => {
    setCUserId(null); setCName(''); setCX1(''); setCY1(''); setCX2(''); setCY2('');
    setCTimeout('30'); setCIsFree(true); setCExpiresAt(''); setFormErr('');
  };

  const openCreate = () => {
    resetForm();
    setEditingZone(null);
    setShowCreate(true);
  };

  const openEdit = (z: AdminZone) => {
    setEditingZone(z);
    setCUserId(z.user_id);
    setCName(z.name);
    setCX1(String(z.x_min)); setCY1(String(z.y_min));
    setCX2(String(z.x_max)); setCY2(String(z.y_max));
    setCTimeout(String(z.eviction_timeout_sec));
    setCIsFree(z.is_free);
    // Convert ISO string to datetime-local format (trim seconds+tz)
    setCExpiresAt(z.expires_at ? z.expires_at.slice(0, 16) : '');
    setFormErr('');
    setShowCreate(true);
  };

  const handleSave = async () => {
    const x1 = parseInt(cX1, 10); const y1 = parseInt(cY1, 10);
    const x2 = parseInt(cX2, 10); const y2 = parseInt(cY2, 10);
    const timeout = parseInt(cTimeout, 10);
    if (!cUserId || cUserId <= 0) { setFormErr(t('admin.zones.errUserId')); return; }
    if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) { setFormErr(t('zone.errInvalidCoords')); return; }
    setSaving(true);
    setFormErr('');
    const payload = {
      user_id: cUserId,
      name: cName.trim(),
      x_min: Math.min(x1, x2), y_min: Math.min(y1, y2),
      x_max: Math.max(x1, x2), y_max: Math.max(y1, y2),
      eviction_timeout_sec: isNaN(timeout) || timeout <= 0 ? 30 : timeout,
      is_free: cIsFree,
      expires_at: !cIsFree && cExpiresAt ? new Date(cExpiresAt).toISOString() : null,
    };
    try {
      if (editingZone) {
        await adminUpdateZone(editingZone.id, payload);
      } else {
        await adminCreateZone(payload);
      }
      setShowCreate(false);
      load();
    } catch (e: any) {
      setFormErr(e.response?.data?.error || t(editingZone ? 'admin.zones.errUpdate' : 'admin.zones.errCreate'));
    } finally {
      setSaving(false);
    }
  };

  const pages = Math.ceil(total / limit);

  return (
    <div className="admin-tab">
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <form className="admin-search" style={{ flex: 1, margin: 0 }} onSubmit={handleSearch}>
          <input
            className="auth-form__input"
            placeholder={t('admin.zones.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button className="admin-btn" type="submit">{t('admin.users.searchBtn')}</button>
        </form>
        <button className="admin-btn" onClick={openCreate}>{t('admin.zones.createBtn')}</button>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {loading && <div className="admin-dim">{t('admin.users.loadingText')}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.zones.colId')}</th>
              <th>{t('admin.zones.colOwner')}</th>
              <th>{t('admin.zones.colName')}</th>
              <th>{t('admin.zones.colCoords')}</th>
              <th>{t('admin.zones.colTimeout')}</th>
              <th>{t('admin.zones.colStatus')}</th>
              <th>{t('admin.zones.colExpires')}</th>
              <th>{t('admin.users.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {zones.map(z => {
              const active = z.is_free || (z.expires_at != null && new Date(z.expires_at) > new Date());
              return (
                <tr key={z.id} style={{ opacity: active ? 1 : 0.55 }}>
                  <td>{z.id}</td>
                  <td className="admin-cell-email">{z.user_email}</td>
                  <td>{z.name || `#${z.id}`}</td>
                  <td style={{ fontSize: '11px' }}>
                    ({z.x_min},{z.y_min})–({z.x_max},{z.y_max})<br />
                    <span className="admin-dim">{(z.x_max - z.x_min + 1)}×{(z.y_max - z.y_min + 1)}</span>
                  </td>
                  <td>{z.eviction_timeout_sec}s</td>
                  <td>
                    {z.is_free
                      ? <span className="admin-badge admin-badge--created">{t('admin.zones.statusFree')}</span>
                      : active
                        ? <span className="admin-badge admin-badge--move">{t('admin.zones.statusActive')}</span>
                        : <span className="admin-badge admin-badge--deleted">{t('admin.zones.statusPending')}</span>}
                  </td>
                  <td style={{ fontSize: '11px' }}>
                    {z.is_free ? '∞' : z.expires_at ? new Date(z.expires_at).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button
                      className="admin-btn admin-btn--sm"
                      onClick={() => openEdit(z)}
                    >
                      {t('admin.users.editBtn')}
                    </button>
                    {' '}
                    <button
                      className="admin-btn admin-btn--sm admin-btn--danger"
                      onClick={() => handleDelete(z)}
                    >
                      {t('admin.users.deleteBtn')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="admin-pagination">
          <button className="admin-btn admin-btn--sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>←</button>
          <span className="admin-dim">{page + 1} / {pages}</span>
          <button className="admin-btn admin-btn--sm" disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>→</button>
        </div>
      )}

      {/* Map selection overlay */}
      {showMap && (
        <AdminMapSelectOverlay
          initialX1={parseInt(cX1, 10) || undefined}
          initialY1={parseInt(cY1, 10) || undefined}
          initialX2={parseInt(cX2, 10) || undefined}
          initialY2={parseInt(cY2, 10) || undefined}
          onSelect={(x1, y1, x2, y2) => {
            setCX1(String(x1)); setCY1(String(y1));
            setCX2(String(x2)); setCY2(String(y2));
            setShowMap(false);
            setShowCreate(true);
          }}
          onCancel={() => { setShowMap(false); setShowCreate(true); }}
        />
      )}

      {/* Create zone modal */}
      {showCreate && (
        <div className="admin-modal-backdrop" onClick={() => setShowCreate(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ minWidth: '320px' }}>
            <div className="admin-modal__title">
              {editingZone ? t('admin.zones.editTitle', { id: editingZone.id }) : t('admin.zones.createTitle')}
            </div>

            <label className="auth-form__label">{t('admin.zones.userIdLabel')}</label>
            <UserSearchInput
              userId={cUserId}
              onChange={(id) => setCUserId(id)}
            />

            <label className="auth-form__label" style={{ marginTop: '8px' }}>{t('zone.namePlaceholder')}</label>
            <input
              className="auth-form__input"
              placeholder={t('zone.namePlaceholder')}
              value={cName}
              onChange={e => setCName(e.target.value)}
            />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
              <label className="auth-form__label" style={{ margin: 0 }}>{t('admin.zones.coordsLabel')}</label>
              <button
                className="admin-btn admin-btn--sm"
                type="button"
                onClick={() => { setShowCreate(false); setShowMap(true); }}
              >
                {t('zone.drawBtn')}
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginTop: '4px' }}>
              <input className="auth-form__input" placeholder="X min" value={cX1} onChange={e => setCX1(e.target.value)} />
              <input className="auth-form__input" placeholder="Y min" value={cY1} onChange={e => setCY1(e.target.value)} />
              <input className="auth-form__input" placeholder="X max" value={cX2} onChange={e => setCX2(e.target.value)} />
              <input className="auth-form__input" placeholder="Y max" value={cY2} onChange={e => setCY2(e.target.value)} />
            </div>

            <label className="auth-form__label" style={{ marginTop: '8px' }}>{t('zone.timeoutLabel')}</label>
            <input
              className="auth-form__input"
              type="number"
              min={5}
              value={cTimeout}
              onChange={e => setCTimeout(e.target.value)}
            />

            <label className="admin-checkbox" style={{ margin: '10px 0 6px' }}>
              <input type="checkbox" checked={cIsFree} onChange={e => setCIsFree(e.target.checked)} />
              <span>{t('admin.zones.isFreeLabel')}</span>
            </label>

            {!cIsFree && (
              <>
                <label className="auth-form__label">{t('admin.zones.expiresAtLabel')}</label>
                <input
                  className="auth-form__input"
                  type="datetime-local"
                  value={cExpiresAt}
                  onChange={e => setCExpiresAt(e.target.value)}
                />
              </>
            )}

            {formErr && <div className="admin-error" style={{ marginTop: '8px' }}>{formErr}</div>}

            <div className="admin-modal__actions">
              <button className="admin-btn" onClick={handleSave} disabled={saving}>
                {saving ? t('common.saving') : editingZone ? t('admin.users.saveBtn') : t('admin.zones.createBtn')}
              </button>
              <button className="admin-btn admin-btn--ghost" onClick={() => setShowCreate(false)}>
                {t('admin.users.cancelBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Maze tab ────────────────────────────────────────────────────────────────

const MazeTab: React.FC = () => {
  const { t } = useTranslation();
  const [showMap,    setShowMap]    = useState(false);
  const [x1, setX1] = useState<number | null>(null);
  const [y1, setY1] = useState<number | null>(null);
  const [x2, setX2] = useState<number | null>(null);
  const [y2, setY2] = useState<number | null>(null);
  const [name,      setName]      = useState('maze');
  const [wallColor, setWallColor] = useState('#000000');
  const [animate,   setAnimate]   = useState(false);
  const [paceMs,    setPaceMs]    = useState(60);
  const [loading,   setLoading]   = useState(false);
  const [result,    setResult]    = useState<MazeResult | null>(null);
  const [error,     setError]     = useState('');

  const hasArea = x1 !== null && y1 !== null && x2 !== null && y2 !== null;

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasArea) { setError(t('admin.maze.selectAreaError')); return; }
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const res = await generateMaze({
        name,
        x_min: Math.min(x1!, x2!),
        y_min: Math.min(y1!, y2!),
        x_max: Math.max(x1!, x2!),
        y_max: Math.max(y1!, y2!),
        wall_color: wallColor,
        pace_ms: animate ? paceMs : undefined,
      });
      setResult(res.data);
    } catch (e: any) {
      setError(e.response?.data?.error || t('admin.maze.error'));
    } finally {
      setLoading(false);
    }
  };

  const w = hasArea ? Math.abs(x2! - x1!) + 1 : 0;
  const h = hasArea ? Math.abs(y2! - y1!) + 1 : 0;

  return (
    <div className="admin-tab admin-import">
      <p className="admin-dim" style={{ marginBottom: '1rem', fontSize: '0.72rem' }}>
        {t('admin.maze.hint')}
      </p>

      {showMap && (
        <AdminMapSelectOverlay
          initialX1={x1 ?? undefined} initialY1={y1 ?? undefined}
          initialX2={x2 ?? undefined} initialY2={y2 ?? undefined}
          onSelect={(ax1, ay1, ax2, ay2) => {
            setX1(ax1); setY1(ay1); setX2(ax2); setY2(ay2);
            setShowMap(false);
          }}
          onCancel={() => setShowMap(false)}
        />
      )}

      <form className="admin-import-form" onSubmit={handleGenerate}>
        <div className="admin-import-left">
          <div className="auth-form__field">
            <label className="auth-form__label">{t('admin.maze.nameLabel')}</label>
            <input
              className="auth-form__input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('admin.maze.namePlaceholder')}
            />
          </div>
          <div className="auth-form__field">
            <label className="auth-form__label">{t('admin.maze.wallColorLabel')}</label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="color"
                value={wallColor}
                onChange={e => setWallColor(e.target.value)}
                style={{ width: '2.5rem', height: '2rem', padding: '0', border: 'none', cursor: 'pointer' }}
              />
              <input
                className="auth-form__input"
                value={wallColor}
                onChange={e => setWallColor(e.target.value)}
                style={{ flex: 1, fontFamily: 'monospace' }}
              />
            </div>
          </div>
          <div className="auth-form__field">
            <label className="auth-form__label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <input
                type="checkbox"
                checked={animate}
                onChange={e => setAnimate(e.target.checked)}
              />
              {t('admin.maze.animateLabel')}
            </label>
            {animate && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.4rem' }}>
                <input
                  type="range" min={10} max={2000} step={10}
                  value={paceMs}
                  onChange={e => setPaceMs(Number(e.target.value))}
                  style={{ flex: 1 }}
                />
                <span className="admin-dim" style={{ fontSize: '0.75rem', minWidth: '6rem' }}>
                  {t('admin.maze.animatePaceMs', { ms: paceMs })}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="admin-import-right">
          <div className="auth-form__field">
            <button
              type="button"
              className="admin-btn"
              onClick={() => setShowMap(true)}
            >
              {t('admin.maze.selectBtn')}
            </button>
            {hasArea && (
              <p className="admin-dim" style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                {t('admin.maze.selectedArea', {
                  x1: Math.min(x1!, x2!), y1: Math.min(y1!, y2!),
                  x2: Math.max(x1!, x2!), y2: Math.max(y1!, y2!),
                  w, h,
                })}
              </p>
            )}
          </div>

          {error && <p style={{ color: '#f66', fontSize: '0.8rem', marginBottom: '0.5rem' }}>{error}</p>}
          {result && (
            <p style={{ color: '#4f4', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
              {t('admin.maze.result', {
                zone_id: result.zone_id,
                walls:   result.walls_created,
                ex:      result.entrance_x,
                ey:      result.entrance_y,
                exitx:   result.exit_x,
                exity:   result.exit_y,
              })}
            </p>
          )}

          <button className="admin-btn" type="submit" disabled={loading || !hasArea}>
            {loading ? t('admin.maze.generatingBtn') : t('admin.maze.generateBtn')}
          </button>
        </div>
      </form>
    </div>
  );
};

type DialogueActorKey = 'left' | 'right';
type DialogueStepType = 'say' | 'jump';

interface DialogueActorState {
  mode: 'existing' | 'new';
  point: AdminPoint | null;
  pointId: number | null;
  userId: number | null;
  userEmail: string;
  name: string;
  x: string;
  y: string;
}

interface DialogueStep {
  id: string;
  actor: DialogueActorKey;
  type: DialogueStepType;
  text: string;
  style: SpeechBubbleStyle;
  delayMs: string;
  jumpWithSay: boolean;
}

const makeActorState = (): DialogueActorState => ({
  mode: 'existing',
  point: null,
  pointId: null,
  userId: null,
  userEmail: '',
  name: '',
  x: '',
  y: '',
});

const makeStep = (actor: DialogueActorKey = 'left'): DialogueStep => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  actor,
  type: 'say',
  text: '',
  style: DEFAULT_SPEECH_STYLE,
  delayMs: '0',
  jumpWithSay: false,
});

interface DialogueScenarioFile {
  version: 1;
  exported_at: string;
  actors: Record<DialogueActorKey, DialogueActorState>;
  steps: DialogueStep[];
}

interface DialoguePreviewStep extends DialogueStep {
  index: number;
  actorLabel: string;
  startMs: number;
  endMs: number;
}

const DialogueTab: React.FC = () => {
  const { t } = useTranslation();
  const [actors, setActors] = useState<Record<DialogueActorKey, DialogueActorState>>({
    left : makeActorState(),
    right: makeActorState(),
  });
  const [steps, setSteps] = useState<DialogueStep[]>([makeStep('left'), makeStep('right')]);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(false);
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const [mapMode, setMapMode] = useState<{ actor: DialogueActorKey; mode: DialogueMapMode } | null>(null);
  const stopRef = useRef(false);
  const importRef = useRef<HTMLInputElement>(null);

  const patchActor = (key: DialogueActorKey, patch: Partial<DialogueActorState>) => {
    setActors(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const bindExistingPoint = useCallback((key: DialogueActorKey, point: AdminPoint) => {
    patchActor(key, {
      mode: 'existing',
      point,
      pointId: point.id,
      userId: point.user_id,
      userEmail: point.user_email,
      name: point.name,
      x: String(point.x),
      y: String(point.y),
    });
  }, []);

  const updateStep = (id: string, patch: Partial<DialogueStep>) => {
    setSteps(prev => prev.map(step => step.id === id ? { ...step, ...patch } : step));
  };

  const removeStep = (id: string) => {
    setSteps(prev => prev.length > 1 ? prev.filter(step => step.id !== id) : prev);
  };

  const waitMs = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms));

  const previewSteps: DialoguePreviewStep[] = (() => {
    let cursor = 0;
    return steps.map((step, index) => {
      const delayMs = Math.max(0, parseInt(step.delayMs, 10) || 0);
      const startMs = cursor + delayMs;
      const actionDuration = step.type === 'say' ? 1200 : 450;
      const endMs = startMs + actionDuration;
      cursor = startMs;
      return {
        ...step,
        index,
        actorLabel: step.actor === 'left' ? t('admin.dialogue.actorA') : t('admin.dialogue.actorB'),
        startMs,
        endMs,
      };
    });
  })();

  const timelineSpanMs = Math.max(
    1000,
    ...previewSteps.map(step => step.endMs),
  );

  const formatTimelineMs = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const tenths = Math.floor((ms % 1000) / 100);
    if (minutes > 0) return `${minutes}:${String(seconds).padStart(2, '0')}.${tenths}`;
    return `${seconds}.${tenths}s`;
  };

  const handlePlay = async () => {
    setError('');
    stopRef.current = false;

    for (const step of steps) {
      const actor = actors[step.actor];
      if (!actor.pointId) {
        setError(t('admin.dialogue.errAllActorsReady'));
        return;
      }
      if (step.type === 'say' && !step.text.trim()) {
        setError(t('admin.dialogue.errEmptySpeech'));
        return;
      }
    }

    setPlaying(true);
    try {
      for (const step of steps) {
        if (stopRef.current) break;
        const delayMs = Math.max(0, parseInt(step.delayMs, 10) || 0);
        if (delayMs > 0) await waitMs(delayMs);
        if (stopRef.current) break;

        setActiveStepId(step.id);
        const actor = actors[step.actor];
        await adminEmitDialog({
          point_id: actor.pointId!,
          text: step.type === 'say' ? step.text.trim() : undefined,
          style: step.type === 'say' ? step.style : undefined,
          jump: step.type === 'jump' || (step.type === 'say' && step.jumpWithSay),
        });
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || t('admin.dialogue.errPlay'));
    } finally {
      setActiveStepId(null);
      setPlaying(false);
      stopRef.current = false;
    }
  };

  const handleStop = () => {
    stopRef.current = true;
    setPlaying(false);
    setActiveStepId(null);
  };

  const fetchAdminPointById = useCallback(async (pointId: number) => {
    const res = await getAdminPoints(String(pointId), 20, 0);
    const point = (res.data.points ?? []).find(item => item.id === pointId);
    if (!point) throw new Error('point not found');
    return point;
  }, []);

  const handlePickExistingOnMap = useCallback(async (key: DialogueActorKey, pointId: number) => {
    setError('');
    try {
      const point = await fetchAdminPointById(pointId);
      bindExistingPoint(key, point);
      setMapMode(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || t('admin.dialogue.errPointLookup'));
    }
  }, [bindExistingPoint, fetchAdminPointById, t]);

  const handlePickPositionOnMap = useCallback(async (key: DialogueActorKey, x: number, y: number) => {
    const actor = actors[key];
    setError('');
    try {
      if (actor.mode === 'new') {
        if (!actor.userId) {
          setError(t('admin.dialogue.errUserRequired'));
          return;
        }
        const res = await adminCreatePoint({
          user_id: actor.userId,
          name: actor.name.trim(),
          x,
          y,
        });
        bindExistingPoint(key, {
          ...res.data,
          user_email: actor.userEmail,
        });
      } else {
        if (!actor.pointId) {
          setError(t('admin.dialogue.errPointRequired'));
          return;
        }
        const res = await adminUpdatePoint(actor.pointId, {
          name: actor.name.trim(),
          x,
          y,
        });
        bindExistingPoint(key, {
          ...actor.point,
          ...res.data,
          user_email: actor.userEmail || actor.point?.user_email || '',
        });
      }
      setMapMode(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || t('admin.dialogue.errSavePoint'));
    }
  }, [actors, bindExistingPoint, t]);

  const handleExport = () => {
    const payload: DialogueScenarioFile = {
      version: 1,
      exported_at: new Date().toISOString(),
      actors,
      steps,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dialogue-scenario-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleImportClick = () => importRef.current?.click();

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as Partial<DialogueScenarioFile>;
      if (parsed.version !== 1 || !parsed.actors || !Array.isArray(parsed.steps)) {
        throw new Error(t('admin.dialogue.errImportInvalid'));
      }
      setActors({
        left: { ...makeActorState(), ...parsed.actors.left },
        right: { ...makeActorState(), ...parsed.actors.right },
      });
      setSteps(parsed.steps.length > 0 ? parsed.steps.map(step => ({
        ...makeStep(step.actor === 'right' ? 'right' : 'left'),
        ...step,
      })) : [makeStep('left')]);
      setError('');
    } catch (err: any) {
      setError(err?.message || t('admin.dialogue.errImportInvalid'));
    } finally {
      e.target.value = '';
    }
  };

  const renderActorCard = (key: DialogueActorKey, title: string) => {
    const actor = actors[key];
    return (
      <div className="admin-dialogue-card">
        <div className="admin-dialogue-card__head">
          <strong>{title}</strong>
          {actor.pointId && <span className="admin-dim">#{actor.pointId}</span>}
        </div>

        <div className="admin-toggle-group" style={{ marginBottom: '0.75rem' }}>
          <button
            type="button"
            className={`admin-btn admin-btn--sm${actor.mode === 'existing' ? ' admin-btn--active' : ''}`}
            onClick={() => patchActor(key, { mode: 'existing' })}
          >
            {t('admin.dialogue.pickExistingBtn')}
          </button>
          <button
            type="button"
            className={`admin-btn admin-btn--sm${actor.mode === 'new' ? ' admin-btn--active' : ''}`}
            onClick={() => patchActor(key, { mode: 'new', point: null, pointId: null })}
          >
            {t('admin.dialogue.createNewBtn')}
          </button>
        </div>

        {actor.mode === 'existing' ? (
          <>
            <label className="auth-form__label">{t('admin.dialogue.pointLabel')}</label>
            <button className="admin-btn" type="button" onClick={() => setMapMode({ actor: key, mode: 'pick-existing' })}>
              {t('admin.dialogue.pickOnMapBtn')}
            </button>
          </>
        ) : (
          <>
            <label className="auth-form__label">{t('admin.dialogue.ownerLabel')}</label>
            <UserSearchInput
              userId={actor.userId}
              onChange={(id, email) => patchActor(key, { userId: id, userEmail: email })}
            />
          </>
        )}

        <label className="auth-form__label" style={{ marginTop: '0.75rem' }}>{t('admin.dialogue.nameLabel')}</label>
        <input
          className="auth-form__input"
          value={actor.name}
          onChange={e => patchActor(key, { name: e.target.value })}
          placeholder={t('admin.dialogue.namePlaceholder')}
        />

        <div className="admin-dialogue-grid">
          <div>
            <label className="auth-form__label">{t('admin.dialogue.xLabel')}</label>
            <input
              className="auth-form__input"
              value={actor.x}
              readOnly
              placeholder="0"
            />
          </div>
          <div>
            <label className="auth-form__label">{t('admin.dialogue.yLabel')}</label>
            <input
              className="auth-form__input"
              value={actor.y}
              readOnly
              placeholder="0"
            />
          </div>
        </div>

        {actor.mode === 'new' ? (
          <button className="admin-btn" type="button" onClick={() => {
            if (!actor.userId) { setError(t('admin.dialogue.errUserRequired')); return; }
            setMapMode({ actor: key, mode: 'pick-position' });
          }}>
            {t('admin.dialogue.createOnMapBtn')}
          </button>
        ) : (
          <button className="admin-btn" type="button" onClick={() => {
            if (!actor.pointId) { setError(t('admin.dialogue.errPointRequired')); return; }
            setMapMode({ actor: key, mode: 'pick-position' });
          }}>
            {t('admin.dialogue.moveOnMapBtn')}
          </button>
        )}

        {(actor.point || actor.pointId) && (
          <div className="admin-dim" style={{ marginTop: '0.5rem' }}>
            {(actor.point?.user_email || actor.userEmail || '—')} · ({actor.x || '—'}, {actor.y || '—'})
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="admin-tab">
      <p className="admin-dim admin-dialogue-hint">{t('admin.dialogue.hint')}</p>

      <div className="admin-dialogue-actors">
        {renderActorCard('left', t('admin.dialogue.actorA'))}
        {renderActorCard('right', t('admin.dialogue.actorB'))}
      </div>

      <div className="admin-dialogue-script">
        <div className="admin-dialogue-script__head">
          <strong>{t('admin.dialogue.scriptTitle')}</strong>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <input
              ref={importRef}
              type="file"
              accept="application/json"
              style={{ display: 'none' }}
              onChange={handleImportFile}
            />
            <button className="admin-btn admin-btn--sm" type="button" onClick={handleImportClick}>
              {t('admin.dialogue.importBtn')}
            </button>
            <button className="admin-btn admin-btn--sm" type="button" onClick={handleExport}>
              {t('admin.dialogue.exportBtn')}
            </button>
            <button className="admin-btn admin-btn--sm" type="button" onClick={() => setSteps(prev => [...prev, makeStep('left')])}>
              {t('admin.dialogue.addStepBtn')}
            </button>
            <button className="admin-btn admin-btn--sm" type="button" onClick={handlePlay} disabled={playing}>
              {playing ? t('admin.dialogue.playingBtn') : t('admin.dialogue.playBtn')}
            </button>
            <button className="admin-btn admin-btn--sm admin-btn--ghost" type="button" onClick={handleStop} disabled={!playing}>
              {t('admin.dialogue.stopBtn')}
            </button>
          </div>
        </div>

        {error && <div className="admin-error">{error}</div>}

        <div className="admin-dialogue-timeline">
          <div className="admin-dialogue-timeline__head">
            <strong>{t('admin.dialogue.timelineTitle')}</strong>
            <span className="admin-dim">
              {t('admin.dialogue.timelineDuration', { duration: formatTimelineMs(timelineSpanMs) })}
            </span>
          </div>

          <div className="admin-dialogue-timeline__track">
            {previewSteps.map(step => (
              <div
                key={`track-${step.id}`}
                className={`admin-dialogue-timeline__bar admin-dialogue-timeline__bar--${step.actor}${activeStepId === step.id ? ' admin-dialogue-timeline__bar--active' : ''}`}
                style={{
                  left: `${(step.startMs / timelineSpanMs) * 100}%`,
                  width: `${Math.max(2, ((step.endMs - step.startMs) / timelineSpanMs) * 100)}%`,
                }}
                title={`${step.actorLabel} · ${formatTimelineMs(step.startMs)}`}
              />
            ))}
          </div>

          <div className="admin-dialogue-timeline__list">
            {previewSteps.map(step => (
              <div key={`preview-${step.id}`} className={`admin-dialogue-timeline__item${activeStepId === step.id ? ' admin-dialogue-timeline__item--active' : ''}`}>
                <span className="admin-dialogue-timeline__time">{formatTimelineMs(step.startMs)}</span>
                <span className={`admin-dialogue-timeline__actor admin-dialogue-timeline__actor--${step.actor}`}>{step.actorLabel}</span>
                <span className="admin-dialogue-timeline__cmd">
                  {step.type === 'say'
                    ? t('admin.dialogue.timelineSayPreview', { text: step.text || '…' })
                    : t('admin.dialogue.timelineJumpPreview')}
                </span>
                {step.type === 'say' && step.style !== DEFAULT_SPEECH_STYLE && (
                  <span className="admin-dialogue-timeline__flag">
                    {t('speech.stylePreview', { style: t(SPEECH_STYLE_OPTIONS.find(option => option.id === step.style)?.labelKey || 'speech.styles.default') })}
                  </span>
                )}
                {step.jumpWithSay && step.type === 'say' && (
                  <span className="admin-dialogue-timeline__flag">{t('admin.dialogue.timelineJumpFlag')}</span>
                )}
                {Number(step.delayMs) > 0 && (
                  <span className="admin-dialogue-timeline__flag">
                    {t('admin.dialogue.timelineDelayFlag', { delay: formatTimelineMs(Math.max(0, parseInt(step.delayMs, 10) || 0)) })}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="admin-dialogue-steps">
          {steps.map((step, index) => (
            <div key={step.id} className={`admin-dialogue-step${activeStepId === step.id ? ' admin-dialogue-step--active' : ''}`}>
              <div className="admin-dialogue-step__row admin-dialogue-step__row--top">
                <span className="admin-dim">{index + 1}.</span>
                <select
                  className="auth-form__input"
                  value={step.actor}
                  onChange={e => updateStep(step.id, { actor: e.target.value as DialogueActorKey })}
                >
                  <option value="left">{t('admin.dialogue.actorA')}</option>
                  <option value="right">{t('admin.dialogue.actorB')}</option>
                </select>
                <select
                  className="auth-form__input"
                  value={step.type}
                  onChange={e => updateStep(step.id, { type: e.target.value as DialogueStepType })}
                >
                  <option value="say">{t('admin.dialogue.cmdSay')}</option>
                  <option value="jump">{t('admin.dialogue.cmdJump')}</option>
                </select>
                <input
                  className="auth-form__input"
                  type="number"
                  min={0}
                  value={step.delayMs}
                  onChange={e => updateStep(step.id, { delayMs: e.target.value })}
                  placeholder="0"
                />
                <button className="admin-btn admin-btn--sm admin-btn--danger" type="button" onClick={() => removeStep(step.id)}>
                  {t('admin.dialogue.deleteStepBtn')}
                </button>
              </div>

              {step.type === 'say' && (
                <>
                  <textarea
                    className="auth-form__input admin-dialogue-textarea"
                    value={step.text}
                    onChange={e => updateStep(step.id, { text: e.target.value })}
                    placeholder={t('admin.dialogue.textPlaceholder')}
                  />
                  <div className="admin-dialogue-step__controls">
                    <div className="admin-dialogue-step__style">
                      <label className="auth-form__label">{t('speech.styleLabel')}</label>
                      <select
                        className="auth-form__input admin-dialogue-style-select"
                        value={step.style}
                        onChange={e => updateStep(step.id, { style: e.target.value as SpeechBubbleStyle })}
                        title={t('speech.styleLabel')}
                      >
                        {SPEECH_STYLE_OPTIONS.map(option => (
                          <option
                            key={option.id}
                            value={option.id}
                          >
                            {t(option.labelKey)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <label
                      className="admin-checkbox admin-dialogue-step__checkbox"
                    >
                      <input
                        type="checkbox"
                        checked={step.jumpWithSay}
                        onChange={e => updateStep(step.id, { jumpWithSay: e.target.checked })}
                      />
                      <span>{t('admin.dialogue.jumpWithSayLabel')}</span>
                    </label>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {mapMode && (
        <DialogueMapOverlay
          actorLabel={mapMode.actor === 'left' ? t('admin.dialogue.actorA') : t('admin.dialogue.actorB')}
          mode={mapMode.mode}
          initialX={parseInt(actors[mapMode.actor].x, 10) || undefined}
          initialY={parseInt(actors[mapMode.actor].y, 10) || undefined}
          selectedX={parseInt(actors[mapMode.actor].x, 10) || undefined}
          selectedY={parseInt(actors[mapMode.actor].y, 10) || undefined}
          onPickExisting={(pointId) => handlePickExistingOnMap(mapMode.actor, pointId)}
          onPickPosition={(x, y) => handlePickPositionOnMap(mapMode.actor, x, y)}
          onCancel={() => setMapMode(null)}
        />
      )}
    </div>
  );
};

// ─── Connections tab ──────────────────────────────────────────────────────────

const formatDuration = (sec: number): string => {
  if (sec < 60)   return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
};

const ConnectionsTab: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<ConnectionStats>({ total: 0, players: 0, observers: 0 });
  const [error, setError] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [items, setItems] = useState<ConnectionInfo[]>([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 100;

  // Poll lightweight stats endpoint every 3 s — O(1) on the server.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      getConnectionStats()
        .then(res => { if (!cancelled) setStats(res.data); })
        .catch(() => { if (!cancelled) setError(t('admin.connections.loadError')); });
    };
    load();
    const id = setInterval(load, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [t]);

  const loadList = useCallback((nextPage: number) => {
    setListLoading(true);
    getConnections(pageSize, nextPage * pageSize)
      .then(res => {
        setItems(res.data.connections ?? []);
        setTotal(res.data.total ?? 0);
        setPage(nextPage);
      })
      .catch(() => setError(t('admin.connections.loadError')))
      .finally(() => setListLoading(false));
  }, [t]);

  const toggleList = () => {
    if (listOpen) {
      setListOpen(false);
      return;
    }
    setListOpen(true);
    loadList(0);
  };

  const from = total === 0 ? 0 : page * pageSize + 1;
  const to   = Math.min(total, (page + 1) * pageSize);

  return (
    <div className="admin-tab">
      {error && <div className="admin-error">{error}</div>}

      <div style={{
        display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem',
      }}>
        <div className="admin-stat-card">
          <div className="admin-stat-card__value">{stats.total}</div>
          <div className="admin-stat-card__label">{t('admin.connections.totalLabel')}</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-card__value">{stats.players}</div>
          <div className="admin-stat-card__label">{t('admin.connections.playersLabel')}</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-card__value">{stats.observers}</div>
          <div className="admin-stat-card__label">{t('admin.connections.observersLabel')}</div>
        </div>
      </div>

      <div style={{ fontSize: 11, opacity: 0.55, marginBottom: '0.5rem' }}>
        {t('admin.connections.refreshHint')}
      </div>

      <button className="admin-btn admin-btn--sm" onClick={toggleList}>
        {listOpen ? t('admin.connections.hideList') : t('admin.connections.showList')}
      </button>

      {listOpen && (
        <>
          {listLoading && <div className="admin-dim" style={{ marginTop: '0.5rem' }}>…</div>}
          {!listLoading && items.length === 0 && (
            <div className="admin-dim" style={{ marginTop: '0.5rem' }}>
              {t('admin.connections.noConnections')}
            </div>
          )}
          {!listLoading && items.length > 0 && (
            <>
              <div className="admin-table-wrap" style={{ marginTop: '0.75rem' }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>{t('admin.connections.colType')}</th>
                      <th>{t('admin.connections.colUser')}</th>
                      <th>{t('admin.connections.colPoint')}</th>
                      <th>{t('admin.connections.colIP')}</th>
                      <th>{t('admin.connections.colArea')}</th>
                      <th>{t('admin.connections.colDuration')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c, i) => (
                      <tr key={`${c.user_id}-${c.point_id}-${c.connected_at}-${i}`}>
                        <td>
                          <span className={`admin-badge admin-badge--${c.is_observer ? 'deleted' : 'created'}`}>
                            {c.is_observer
                              ? t('admin.connections.typeObserver')
                              : t('admin.connections.typePlayer')}
                          </span>
                        </td>
                        <td>{c.user_id === 0 ? '—' : c.user_id}</td>
                        <td>{c.point_id === 0 ? '—' : c.point_id}</td>
                        <td className="admin-cell-email">{c.remote_addr || '—'}</td>
                        <td>
                          ({c.area_x_min},{c.area_y_min}) – ({c.area_x_max},{c.area_y_max})
                        </td>
                        <td>{formatDuration(c.duration_sec)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginTop: '0.5rem', gap: '0.5rem',
              }}>
                <button
                  className="admin-btn admin-btn--sm admin-btn--ghost"
                  disabled={page === 0}
                  onClick={() => loadList(page - 1)}
                >{t('admin.connections.prev')}</button>
                <span style={{ fontSize: 11, opacity: 0.7 }}>
                  {t('admin.connections.pageInfo', { from, to, total })}
                </span>
                <button
                  className="admin-btn admin-btn--sm admin-btn--ghost"
                  disabled={to >= total}
                  onClick={() => loadList(page + 1)}
                >{t('admin.connections.next')}</button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const AdminPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('users');

  const TABS: { key: Tab; label: string }[] = [
    { key: 'users',  label: t('admin.tabs.users')  },
    { key: 'stats',  label: t('admin.tabs.stats')  },
    { key: 'events', label: t('admin.tabs.events') },
    { key: 'import', label: t('admin.tabs.import') },
    { key: 'zones',  label: t('admin.tabs.zones')  },
    { key: 'maze',   label: t('admin.tabs.maze')   },
    { key: 'dialogue', label: t('admin.tabs.dialogue') },
    { key: 'connections', label: t('admin.tabs.connections') },
  ];

  return (
    <div className="admin-page px-page">
      <div className="admin-panel">
        {/* Header */}
        <div className="admin-header">
          <span className="admin-header__title">{t('admin.title')}</span>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => navigate('/')}>
              {t('common.backToMap')}
            </button>
            <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => navigate('/dashboard')}>
              {t('common.navDashboard')}
            </button>
            <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => navigate('/profile')}>
              {t('common.navProfile')}
            </button>
            <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => navigate('/shop')}>
              {t('common.navShop')}
            </button>
            <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => navigate('/zones')}>
              {t('common.navZones')}
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="admin-tabs">
          {TABS.map(tb => (
            <button
              key={tb.key}
              className={`admin-tab-btn${tab === tb.key ? ' admin-tab-btn--active' : ''}`}
              onClick={() => setTab(tb.key)}
            >{tb.label}</button>
          ))}
        </div>

        {/* Content */}
        <div className="admin-content">
          {tab === 'users'  && <UsersTab />}
          {tab === 'stats'  && <StatsTab />}
          {tab === 'events' && <EventsTab />}
          {tab === 'import' && <ImportTab />}
          {tab === 'zones'  && <ZonesTab />}
          {tab === 'maze'   && <MazeTab />}
          {tab === 'dialogue' && <DialogueTab />}
          {tab === 'connections' && <ConnectionsTab />}
        </div>
      </div>
    </div>
  );
};
